import { AxiosError } from 'axios';
import cloneDeep from 'lodash-es/cloneDeep';
import findIndex from 'lodash-es/findIndex';
import orderBy from 'lodash-es/orderBy';

import { BrandApiContentBrandsListingListRequest } from '@/clients/unifiedApi';
import { SPONSORSHIP_POSITIONS } from '@/constants';
import { Brand, Company } from '@/models/company';
import { SegmentAdTrackingData } from '@/models/segment';
import { ExportMenuPayload, FavoritesItem, SampleRequest, StoreFavoritesItem } from '@/models/shop';
import { SponsorshipWithSegmentTrackingData } from '@/models/sponsorship';
import api from '@/services/api';
import { fetchBrandContent } from '@/services/api/content';
import parser from '@/services/parsers';
import { cancel as cancelRequest } from '@/services/session/request-manager';
import { track } from '@/track';
import { ExtractObjectValues } from '@/utilityTypes/extractObjectValues';
import getNextPage from '@/utils/getNextPage';

import { ShopState } from './shop.state';
import {
  cleanBrand,
  determineCampaignStatus,
  getSponsorshipProductWithSegmentTrackingData,
  parseBrandBadges,
} from './shop.utils';

interface FetchProductDetailActionPayload {
  brandId: number;
  companySlug: string;
  productId: number;
}

interface ToggleFavoritesItemActionPayload {
  productId: number;
  trackingSource: string;
  userActiveCompany: Pick<Company, 'id' | 'name'>;
}

export const setupShopActions = (state: ShopState) => {
  // used in a few spots after updating some purchase list items
  const setUpdatedPurchaseListItem = (updatedItem: StoreFavoritesItem) => {
    const updatedItemIndex = findIndex(state.purchaseList.value, { id: updatedItem.id });

    state.purchaseList.value.splice(updatedItemIndex, 1, updatedItem);
  };

  const addPurchaseListItemToCart = async (item: StoreFavoritesItem) => {
    await api.shop.addPurchaseListItemToCart(item.id);

    setUpdatedPurchaseListItem({
      ...item,
      quantity: null,
    });
  };

  const bulkAddPurchaseListToCart = async (buyerId: number) => {
    const { errors, items } = await api.shop.bulkAddPurchaseListToCart(buyerId);

    items.forEach((item) => {
      setUpdatedPurchaseListItem({
        ...item,
        quantity: null,
      });
    });

    return errors;
  };

  const createFavoritesList = async (payload: { buyer: Pick<Company, 'id' | 'name'> }) => {
    const newFavoritesList = await api.shop.createFavoritesList({ buyer: payload.buyer });

    state.favoritesLists.value.push(newFavoritesList);
  };

  const exportMenu = async (payload: ExportMenuPayload) => {
    const response = await api.shop.exportMenu(payload);

    return response.data;
  };

  const fetchActiveCampaign = async () => {
    try {
      const activeCampaign = await api.shop.getActiveCampaign();

      if (!activeCampaign) {
        // activeCampaign is undefined, but setting it explicitly
        // so it is clear we are resetting the active campaign state
        state.activeCampaign.value = undefined;
        return;
      }

      const campaignStatus = determineCampaignStatus(activeCampaign);

      state.activeCampaign.value = {
        ...activeCampaign,
        status: campaignStatus,
      };
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 404) {
        return;
      }

      throw error;
    }
  };

  const fetchBrand = async (brandId: number) => {
    const brand = await api.shop.getBrand(brandId, false);

    return cleanBrand(brand);
  };

  const fetchBrandActivities = async (page: number) => {
    const activities = await api.shop.getActivitiesFeed(page);
    const existingActivities = cloneDeep(state.brandActivities.value.results);

    if (!activities?.results) {
      return;
    }

    const groups = parser.shop.groupedActivity(activities.results);
    const orderedByRecency = orderBy(existingActivities.concat(groups), ['timestamp'], ['desc']);

    state.brandActivities.value = {
      results: orderedByRecency,
      count: activities.count,
      nextPage: getNextPage(activities.next),
    };

    state.isActivityFeedLoaded.value = true;
  };

  const fetchBrandMenus = async (id: number | string) => {
    const response = await api.shop.getBrandMenus({ id });

    state.brandMenus.value = response;

    return response;
  };

  const fetchBrandPreview = async (brandId: number) => {
    const brand = await api.shop.getBrand(brandId, true);

    return cleanBrand(brand);
  };

  const fetchBrands = async () => {
    const brands = await api.shop.getBrands();

    state.brands.value = brands.map(cleanBrand);
  };

  const fetchNewBrands = async (userActiveCompanyId: number) => {
    const { content } = await api.shop.getNewBrands(userActiveCompanyId);

    if (!content) {
      return;
    }

    state.newBrands.value = content.collection;
  };

  const fetchDeals = async () => {
    state.deals.value = await api.shop.getDeals();
  };

  const fetchFavoritesItems = async (userActiveCompanyId: number) => {
    let currentActiveFavoritesList = state.getActiveFavoritesList.value(userActiveCompanyId);

    if (!currentActiveFavoritesList) {
      await fetchFavoritesLists();
      currentActiveFavoritesList = state.getActiveFavoritesList.value(userActiveCompanyId);
    }

    // check again for favorites items; if we still don't have any, don't do anything
    if (!currentActiveFavoritesList) {
      return;
    }

    const favoritesItems = await api.shop.getFavoritesItems({ listId: currentActiveFavoritesList.id });

    state.favoritesItems.value = favoritesItems;
  };

  const fetchFavoritesLists = async () => {
    const favoritesLists = await api.shop.getFavoritesLists();

    state.favoritesLists.value = favoritesLists;

    return favoritesLists;
  };

  const fetchFeaturedProduct = async (brandId: number) => {
    try {
      return await api.shop.getFeaturedProduct(brandId);
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        // sometimes the brand has no featured products and that's ok
        return;
      }

      throw error;
    }
  };

  const fetchFeaturedProductPreview = async ({
    brandId,
    licenseTypeId,
  }: {
    brandId: number;
    licenseTypeId: number;
  }) => {
    try {
      return await api.shop.getFeaturedProductPreview(brandId, licenseTypeId);
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        // sometimes the brand has no featured products and that's ok
        return;
      }

      throw error;
    }
  };

  const trackImpression = (impressionUrl: string) => {
    return api.shop.getImpressionPixel(impressionUrl);
  };

  const fetchImpressionPixels = (impressionPixelUrls: string[]) => {
    impressionPixelUrls.forEach(api.shop.getImpressionPixel);
  };

  const fetchLegacySponsorships = async (sponsorshipPosition: ExtractObjectValues<typeof SPONSORSHIP_POSITIONS>) => {
    const sponsorships = await api.shop.getSponsorships(sponsorshipPosition);

    // each sponsorship will have Segment tracking data appended
    const sponsorshipsWithSegmentData = sponsorships.map((sponsorship) => {
      const trackingData: SegmentAdTrackingData = {
        adzerk_ad_id: sponsorship.adzerkAdId.toString(),
        adzerk_campaign_id: sponsorship.adzerkCampaignId.toString(),
        adzerk_creative_id: sponsorship.adzerkCreativeId.toString(),
        brand_id: sponsorship.brand.id,
        click_url: sponsorship.clickUrl,
        impression_url: sponsorship.impressionUrl,
        page_load_id: sponsorship.pageLoadId,
        placement_name: sponsorship.positionId,
        product_id: sponsorship.product?.id ?? null,
        target_company_id: Number(sponsorship.targetCompanyId),
        target_user_id: Number(sponsorship.targetUserId),
      };

      const currentSponsorship: SponsorshipWithSegmentTrackingData = {
        ...sponsorship,
        brand: {
          ...cleanBrand(sponsorship.brand),
          tagline: sponsorship.tagline ? sponsorship.tagline : sponsorship.brand.tagline,
        },
        brandId: sponsorship.brand.id,
        product: getSponsorshipProductWithSegmentTrackingData(sponsorship, trackingData),
        trackingData,
        userId: sponsorship.targetUserId,
      };

      return currentSponsorship;
    });

    state.sponsorships.value = sponsorshipsWithSegmentData;
  };

  const fetchPreviewProduct = async (payload: { brandId: number; productId: number }) => {
    return await api.shop.getPreviewProduct(payload);
  };

  const fetchPreviewProducts = async (payload: { brandId: number; licenseTypeId: number }) => {
    const previewProducts = await api.shop.getPreviewProducts(payload);

    state.shopProducts.value = previewProducts;
  };

  const fetchProductDetail = async ({ productId, brandId, companySlug }: FetchProductDetailActionPayload) => {
    return await api.shop.getProductDetail(brandId, productId, companySlug);
  };

  // TODO this is removed in 159 and needs to be removed from the API code too
  const fetchProductDetailsById = async (productId: string) => {
    return await api.shop.getProductDetailsById(productId);
  };

  // eslint-disable-next-line require-await
  const fetchProductFilters = async () => {
    try {
      return api.shop.getProductFilters();
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        return;
      }

      throw error;
    }
  };

  const fetchPublicMenuBrand = async (brand: string) => {
    const fetchedBrand = await api.shop.getPublicMenuBrand(brand);

    return {
      ...fetchedBrand,
      badges: parseBrandBadges(fetchedBrand),
    };
  };

  const fetchPublicMenuFeaturedProduct = async (brand: string) => {
    try {
      return await api.shop.getPublicMenuFeaturedProduct(brand);
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 404) {
        return; // sometimes the brand has no featured product and that's ok
      }

      throw error;
    }
  };

  const fetchPublicMenuProductDetail = async ({ brand, productId }: { brand: string; productId: number }) => {
    return await api.shop.getPublicMenuProductDetail(brand, productId);
  };

  const fetchPublicPromos = async (params = {}) => {
    const promos = await api.shop.getPublicPromos(params);

    state.publicPromos.value = promos.results;

    return promos;
  };

  const fetchPublicMenuProducts = ({ brand, params }: { brand: string; params: string }) => {
    return api.shop.getPublicMenuProducts(brand, params);
  };

  const fetchPurchaseHistory = async ({ buyerId }: { buyerId?: string }) => {
    const response = await api.shop.getPurchaseHistory({ buyerId });

    state.purchaseHistory.value = response.results;
  };

  const fetchPurchaseHistoryItem = async ({ buyerId, productId }: { buyerId: string | number; productId: string }) => {
    const response = await api.shop.getPurchaseHistory({ buyerId, productId });

    if (response.results.length) {
      const [fetchedItem] = response.results;

      const index = state.purchaseHistory.value.findIndex((historyItem) => {
        return historyItem.buyer.id === fetchedItem.buyer.id && historyItem.product.id === fetchedItem.product.id;
      });

      state.purchaseHistory.value.splice(index, 1, fetchedItem);
    }
  };

  const fetchPurchaseList = async (buyerId?: number | string) => {
    const purchaseList = await api.shop.getPurchaseList(buyerId);

    state.purchaseList.value = purchaseList;
  };

  const fetchSellerLicenseTypes = async (brandId: number) => {
    return await api.shop.getSellerLicenseTypes(brandId);
  };

  /**
   * Returns content for the Shop Brands page when the content API is enabled
   * @param payload the stringified query string for the content API
   */
  const fetchShopBrandsContent = async (payload: BrandApiContentBrandsListingListRequest) => {
    return await fetchBrandContent(payload);
  };

  const fetchVendorMenuBrandMenu = async ({ id, params = '' }: { id: number | string; params?: string }) => {
    const vendorBrandMenus = await api.shop.getBrandMenus({ id, params });

    // request may have been cancelled
    if (!vendorBrandMenus) {
      return;
    }

    const [vendorBrandMenu] = vendorBrandMenus;

    const brandIndex = state.brandMenus.value.findIndex((menu) => menu.brand.id === vendorBrandMenu.brand.id);

    const matchingBrand = state.brandMenus.value[brandIndex];

    const updatedBrandMenu = {
      ...matchingBrand,
      isLoading: false,
      productLines: vendorBrandMenu.productLines,
    };

    state.brandMenus.value.splice(brandIndex, 1, updatedBrandMenu);
  };

  const fetchVendorProducts = async (sellerId: number) => {
    const menus = await api.shop.getVendorProducts(sellerId);

    state.brandMenus.value = menus;
  };

  const resetVendorMenuBrandMenu = () => {
    cancelRequest('brandMenus[]');

    state.brandMenus.value = state.brandMenus.value.map((brandMenu) => ({
      ...brandMenu,
      isLoading: true,
      productLines: [],
    }));
  };

  const sendSampleRequest = async (payload: SampleRequest) => {
    return await api.shop.createSampleRequest(payload);
  };

  const setupVendorMenuBrands = (vendorBrands: Brand[]) => {
    state.brandMenus.value = vendorBrands.map((brand: Brand) => ({
      brand,
      isLoading: true,
      productLines: [],
    }));
  };

  const toggleFavoritesItem = async (payload: ToggleFavoritesItemActionPayload) => {
    const { productId, trackingSource, userActiveCompany } = payload;

    let activeFavoritesList = state.favoritesLists.value.find((list) => list.buyerId === userActiveCompany.id);

    if (!state.favoritesItems.value.length) {
      await fetchFavoritesItems(userActiveCompany.id);
      activeFavoritesList = state.getActiveFavoritesList.value(userActiveCompany.id);
    }

    if (!activeFavoritesList) {
      await createFavoritesList({ buyer: userActiveCompany });
      activeFavoritesList = state.getActiveFavoritesList.value(userActiveCompany.id);
    }

    if (!activeFavoritesList) {
      return;
    }

    const itemIndex = state.favoritesItems.value.findIndex((item) => item.productId === productId);
    const itemExists = itemIndex >= 0;
    const newFavoritesItems = [...state.favoritesItems.value];
    let newItem: FavoritesItem;

    if (itemExists) {
      await api.shop.deleteFavoritesItem({ productId, listId: activeFavoritesList.id });
      newFavoritesItems.splice(itemIndex, 1);
    } else {
      newItem = await api.shop.createFavoritesItem({ productId, listId: activeFavoritesList.id });
      newFavoritesItems.push(newItem);
    }

    state.favoritesItems.value = newFavoritesItems;

    const trackingEventName = itemExists ? 'Removed from Store Favorites' : 'Added to Store Favorites';

    track(trackingEventName, {
      product_id: productId,
      company_id: activeFavoritesList.buyerId, // same as user/activeCompanyId
      source: `${trackingSource}`,
    });
  };

  const updateProductLinePositions = async (payload: {
    brandId: number;
    productLinePositions: { [productLineId: number]: number };
  }) => {
    const { brandId, productLinePositions } = payload;

    await api.shop.updateProductLineMenuPositions(
      brandId,
      Object.keys(productLinePositions).map((productLineId) => ({
        id: parseInt(productLineId),
        menuPosition: productLinePositions[productLineId],
      })),
    );

    state.shopProducts.value.forEach((product) => {
      const { productLine } = product;

      product.productLine.menuPosition = productLinePositions[productLine.id] ?? productLine.menuPosition;
    });
  };

  const updateProductPositions = async (payload: {
    brandId: number;
    productPositions: { [productId: number]: number };
  }) => {
    const { brandId, productPositions } = payload;

    await api.shop.updateProductMenuPositions(
      brandId,
      Object.keys(productPositions).map((productId) => ({
        id: parseInt(productId),
        menuPosition: productPositions[productId],
      })),
    );

    state.shopProducts.value.forEach((product) => {
      product.menuPosition = productPositions[product.id] ?? product.menuPosition;

      if (product.parent) {
        product.parent.menuPosition = productPositions[product.parent.id] ?? product.parent.menuPosition;
      }
    });
  };

  const updatePurchaseListItem = async (payload: StoreFavoritesItem) => {
    const response = await api.shop.updatePurchaseListItem(payload);

    setUpdatedPurchaseListItem(response);
  };

  return {
    addPurchaseListItemToCart,
    bulkAddPurchaseListToCart,
    createFavoritesList,
    exportMenu,
    fetchActiveCampaign,
    fetchBrand,
    fetchBrandActivities,
    fetchBrandMenus,
    fetchBrandPreview,
    fetchBrands,
    fetchNewBrands,
    fetchDeals,
    fetchFavoritesItems,
    fetchFavoritesLists,
    fetchFeaturedProduct,
    fetchFeaturedProductPreview,
    fetchImpressionPixels,
    fetchLegacySponsorships,
    fetchPreviewProduct,
    fetchPreviewProducts,
    fetchProductDetail,
    fetchProductDetailsById,
    fetchProductFilters,
    fetchPublicMenuBrand,
    fetchPublicMenuFeaturedProduct,
    fetchPublicMenuProductDetail,
    fetchPublicMenuProducts,
    fetchPublicPromos,
    fetchPurchaseHistory,
    fetchPurchaseHistoryItem,
    fetchPurchaseList,
    fetchSellerLicenseTypes,
    fetchShopBrandsContent,
    fetchVendorMenuBrandMenu,
    fetchVendorProducts,
    resetVendorMenuBrandMenu,
    sendSampleRequest,
    setupVendorMenuBrands,
    toggleFavoritesItem,
    trackImpression,
    updateProductLinePositions,
    updateProductPositions,
    updatePurchaseListItem,
  };
};
